/*
 * Candles by Midnight — exit-intent capture.
 * Desktop: mouse leaves toward the top. Mobile: fast scroll-up or back gesture, with a time fallback.
 * Suppressed via localStorage for N days after close or submit.
 */
if (!customElements.get('exit-intent')) {
  customElements.define(
    'exit-intent',
    class ExitIntent extends HTMLElement {
      connectedCallback() {
        this.key = 'cbm_exit_intent_until';
        this.dialog = this.querySelector('.exitpop__dialog');
        this.days = parseInt(this.dataset.suppressDays, 10) || 14;
        this.shown = false;

        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-exit-close]')) { e.preventDefault(); this.close(true); }
          if (e.target.closest('[data-exit-submit]') || e.target.closest('[data-exit-suppress]')) this.suppress();
          const copy = e.target.closest('[data-exit-copy]');
          if (copy) this.copyCode(copy);
        });
        this.onKey = (e) => { if (e.key === 'Escape' && !this.hidden) this.close(true); };
        document.addEventListener('keydown', this.onKey);

        // Keep focus inside the dialog while it is open. A modal that lets
        // focus escape behind it is unusable with a keyboard or screen reader.
        this.trapFocus = (e) => {
          if (this.hidden || this.contains(e.target)) return;
          const focusable = this.querySelector(
            'input, button, a[href], select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable) focusable.focus();
        };

        // The customer form posts and reloads the page; reopen so the visitor
        // sees the success or error message instead of nothing.
        if (this.querySelector('[data-exit-success]') && /customer_posted=true|contact%5Bemail%5D|#contact_form/.test(location.href + location.hash)) {
          this.open(true);
          return;
        }

        // ?exitpop=preview forces the popup open, ignoring the "don't show
        // again" window, so it can be checked without clearing localStorage.
        if (/[?&]exitpop=preview\b/.test(location.search)) {
          this.open(true);
          return;
        }

        if (window.Shopify && window.Shopify.designMode) {
          const section = this.closest('.shopify-section');
          document.addEventListener('shopify:section:select', (e) => {
            if (section && e.target === section) { this.shown = false; this.open(true); }
          });
          document.addEventListener('shopify:section:deselect', (e) => {
            if (section && e.target === section) { this.close(false); this.shown = false; }
          });
        }

        if (this.suppressed()) return;

        const delay = parseInt(this.dataset.delay, 10) || 0;
        if (delay > 0) {
          this.delayTimer = window.setTimeout(() => this.open(), delay * 1000);
        }

        const scrollPct = parseInt(this.dataset.scroll, 10) || 0;
        if (scrollPct > 0) {
          this.onDepth = () => {
            const max = document.documentElement.scrollHeight - window.innerHeight;
            if (max > 0 && window.scrollY / max >= scrollPct / 100) this.open();
          };
          window.addEventListener('scroll', this.onDepth, { passive: true });
        }

        this.onMouseOut = (e) => {
          if (e.clientY <= 0 && (!e.relatedTarget && !e.toElement)) this.open();
        };
        document.addEventListener('mouseout', this.onMouseOut);

        // Mobile: a fast upward FLICK, not any upward scroll. The previous
        // threshold (60px after 400px depth) fired on ordinary reading —
        // people scroll back up constantly — so the popup felt broken and
        // arbitrary. Now it needs real velocity, meaningful depth, and a
        // minimum dwell time, which together approximate "about to leave".
        this.lastY = window.scrollY;
        this.lastT = Date.now();
        this.armedAt = Date.now();
        this.onScroll = () => {
          const y = window.scrollY;
          const t = Date.now();
          const dy = this.lastY - y;      // positive = scrolling up
          const dt = t - this.lastT || 1;
          const velocity = dy / dt;       // px per ms

          // >1.2 px/ms upward is a deliberate flick, not reading.
          // Require 800px depth and 15s on page so it never greets someone
          // who has only just arrived.
          if (
            velocity > 1.2 &&
            dy > 180 &&
            y > 800 &&
            t - this.armedAt > 15000
          ) {
            this.open();
          }
          this.lastY = y;
          this.lastT = t;
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });

        // Time fallback on mobile only, and long enough to imply real intent.
        this.timer = window.setTimeout(() => {
          if (window.innerWidth < 750 && window.scrollY > 600) this.open();
        }, 45000);
      }

      copyCode(button) {
        const code = button.dataset.exitCopy;
        const done = () => {
          const label = button.textContent;
          button.textContent = button.dataset.copied || 'Copied';
          window.setTimeout(() => { button.textContent = label; }, 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(done, done);
        } else {
          done();
        }
      }

      suppressed() {
        try {
          const until = parseInt(localStorage.getItem(this.key) || '0', 10);
          return Date.now() < until;
        } catch (e) { return false; }
      }

      suppress() {
        try { localStorage.setItem(this.key, String(Date.now() + this.days * 864e5)); } catch (e) {}
      }

      open(force) {
        if (this.shown || (!force && this.suppressed())) return;
        this.shown = true;
        this.hidden = false;
        // Remember what had focus so it can be handed back on close —
        // without this, dismissing the popup drops keyboard users at the top
        // of the document.
        this.lastFocused = document.activeElement;
        document.body.style.overflow = 'hidden';
        const email = this.querySelector('[data-exit-email]');
        if (email) window.setTimeout(() => email.focus(), 60);
        document.addEventListener('focusin', this.trapFocus);
        this.teardownTriggers();
      }

      close(suppress) {
        this.hidden = true;
        document.body.style.overflow = '';
        document.removeEventListener('focusin', this.trapFocus);
        if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
        if (suppress) this.suppress();
      }

      teardownTriggers() {
        document.removeEventListener('mouseout', this.onMouseOut);
        window.removeEventListener('scroll', this.onScroll);
        if (this.onDepth) window.removeEventListener('scroll', this.onDepth);
        if (this.timer) window.clearTimeout(this.timer);
        if (this.delayTimer) window.clearTimeout(this.delayTimer);
      }

      disconnectedCallback() {
        this.teardownTriggers();
        document.removeEventListener('keydown', this.onKey);
      }
    }
  );
}
